import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "@opero/ui";
import { BrandLockup } from "@opero/ui";
import { buildTokenRedirectUrl, tokenStorage } from "@opero/shared";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import { polaris, APP_URL } from "../lib/polaris-client";

type CallbackState =
  | { status: "processing" }
  | { status: "error"; message: string };

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>({ status: "processing" });

  useEffect(() => {
    let cancelled = false;
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    const handle = async () => {
      const queryError = search.get("error") || hash.get("error");
      if (queryError) {
        if (!cancelled) {
          setState({
            status: "error",
            message: humanizeOauthError(queryError),
          });
        }
        return;
      }

      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token") ?? "";
      if (accessToken) {
        const target = resolveAppRedirect(hash.get("redirect_to"));
        window.location.replace(
          buildTokenRedirectUrl(target, accessToken, refreshToken),
        );
        return;
      }

      const signupToken = hash.get("sso_signup_token");
      if (signupToken) {
        try {
          tokenStorage.clearTokens();
          const data = await polaris.auth.signupSso({
            body: { ssoSignupToken: signupToken },
          });
          if (cancelled) return;
          if (data?.setupToken) {
            navigate("/setup", {
              replace: true,
              state: {
                setupToken: data.setupToken,
                expiresIn: data.expiresIn,
              },
            });
            return;
          }
          if (data?.accessToken) {
            const target = resolveAppRedirect(hash.get("redirect_to"));
            window.location.replace(
              buildTokenRedirectUrl(
                target,
                data.accessToken,
                data.refreshToken ?? "",
              ),
            );
            return;
          }
          throw new Error("Signup did not return a session or setup token");
        } catch (err: unknown) {
          if (!cancelled) {
            setState({
              status: "error",
              message: getErrorMessage(
                err,
                "Could not finish SSO signup. The link may have expired.",
              ),
            });
          }
        }
        return;
      }

      if (!cancelled) {
        setState({
          status: "error",
          message: "No SSO session was found in the callback URL.",
        });
      }
    };

    handle();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-800 p-8">
        <div className="mb-6 flex justify-center">
          <BrandLockup />
        </div>
        {state.status === "processing" ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <Spinner size="lg" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Finishing sign in…
            </p>
          </div>
        ) : (
          <div className="space-y-4 text-center">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              Sign-in failed
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {state.message}
            </p>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function resolveAppRedirect(redirectTo: string | null): string {
  if (!redirectTo) return APP_URL;
  try {
    const candidate = new URL(redirectTo, APP_URL);
    const appOrigin = new URL(APP_URL).origin;
    if (candidate.origin === appOrigin) return candidate.toString();
  } catch {
    /* fall through */
  }
  return APP_URL;
}

function humanizeOauthError(code: string): string {
  switch (code) {
    case "access_denied":
      return "You cancelled the sign-in or declined access.";
    case "invalid_request":
      return "The SSO request was malformed. Please try again.";
    case "server_error":
      return "The identity provider returned an error. Please try again.";
    default:
      return code.replace(/[_-]+/g, " ");
  }
}
